import { Trans, useTranslation } from "react-i18next";
import type { ParseKeys } from "i18next";
import { isDeferredSettlement } from "../utils/deferredSettlement";
import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import {
  apiGet,
  apiGetText,
  apiDelete,
  apiPost,
  apiPatch,
  apiPut,
} from "../api/client";
import { Button } from "../components/Button";
import { IconButton } from "../components/IconButton";
import { AppleComboBox } from "../components/AppleComboBox";
import { AppleInput } from "../components/AppleInput";
import { AppleNumberInput } from "../components/AppleNumberInput";
import { AppleCard } from "../components/AppleCard";
import MemoSection from "../components/MemoSection";
import { SaleInfoHeader } from "../components/SaleInfoHeader";
import { ArrowLeftIcon, XMarkIcon } from "@heroicons/react/24/solid";
import {
  ymdTodayLocal,
  formatAnyDateToLocale,
  parseYmdLocal,
} from "../utils/dateUtils";
import { formatNumber, formatMoney } from "../utils/formatNumber";
import { companyRoute } from "../utils/routeHelpers";
import { AuthMe } from "../types/auth";
import UpgradeModal from "../components/UpgradeModal";
import { A, pillClass } from "../styles/appleClasses";
import { AppleDateInput } from "../components/AppleDateInput";
import { usePostHog } from "@posthog/react";
import {
  extractApiDetailOutcome,
  formatClosedThroughDate,
  isClosedPeriodError,
  isDateClosed,
} from "../utils/closedPeriod";
import {
  IDLE_CUSTOMER_PAYMENT_ATTEMPT,
  canonicalCustomerPayment,
  classifyCustomerPaymentFailure,
  clearStoredCustomerPaymentAttempt,
  customerPaymentIdempotencyKey,
  decideCustomerPaymentAttempt,
  fingerprintCustomerPayment,
  readStoredCustomerPaymentAttempt,
  resolveCustomerPaymentAmbiguousFailure,
  resolveCustomerPaymentDefiniteRejection,
  resolveCustomerPaymentSuccess,
  attemptFromStored,
  writeStoredCustomerPaymentAttempt,
  type CustomerPaymentAttempt,
} from "../utils/customerPaymentIdempotency";
import {
  isCashPaymentMethod,
  paymentMethodFeeFraction,
  paymentMethodFixedFee,
} from "../utils/paymentMethod";
import {
  resolveCustomerPaymentFee,
  customerPaymentFeeIntentFields,
  CustomerPaymentPostBody,
} from "../utils/customerPaymentPosting";

/* =========================
   Types
   ========================= */
type BubbleType =
  | "preorder"
  | "fulfilled"
  | "paid"
  | "partial"
  | "unpaid"
  | "voided";

interface Customer {
  id: number | null;
  name: string;
  email: string;
  mobile?: string | null;
  phone?: string | null;
  whatsapp?: string | null;
  is_guest?: boolean;
}

interface SaleLine {
  id: number;
  product_id: number | null;
  product_name: string | null;
  detail: string | null;
  qty: number;
  unit_price: number;
  total: number;
  discount?: number;
  preorder?: boolean;
  item_kind?: "product" | "service";
  fulfilled_date?: string | null;
  fulfilled_qty?: number;
}

interface PaymentMethod {
  id: number;
  name: string;
  type: string;
  // FEE-RATE-SEMANTICS-1 Phase 2B: read the backend-derived effective percent,
  // never the raw fee_rate. The auto-fee preview is non-authoritative.
  fee_rate_percent?: number | null;
  fixed_fee?: number | null;
  account_id: number;
  provider?: string;
  provider_method?: "card" | "alipay" | "wechat_pay" | string;
  online?: boolean;
}

interface Sale {
  id: number;
  date: string;
  customer: Customer | null;
  amount: number;
  status: string | null;
  invoice: string | null;
  invoice_id: string | number | null;
  invoice_provider_status?: string | null;
  invoice_provider_meta?: any;
  invoice_provider_payment_intent_id?: string | null;
  provider_status?: string | null;
  reconcile_required?: boolean;
  reconcile_reason?: string | null;
  stripe_conflict?: boolean;
  stripe_receipt_exceptions?: { id: number; amount: string; currency: string; payment_reference: string; reason: string }[];
  lines: SaleLine[];
  on_account: boolean;
  due_date?: string;
  voided?: boolean;
  edited?: boolean;
  last_edited_at?: string;
  memo?: string | null;
  payment_method_id?: number | null;
  payment_method?: PaymentMethod | null;
  payment_status?: string | null;
  fulfillment_status?: string | null;
  amount_paid?: number;
  status_raw?: string | null;
  via_paylink?: boolean;
  via_gateway?: boolean;
  gateway_provider?: string | null;
  gateway_method?: string | null;
  paylink_url?: string | null;
  tags?: string[];
  payment_conflict?: boolean | null;
  payment_conflict_reason?: string | null;
  payment_conflict_state?: string | null;
}

interface Account {
  id: number;
  name: string;
  kind: string;
}

interface CustomerPayment {
  id: number;
  amount: number;
  payment_date: string;
  invoice_id: number | string;
  payment_account_id?: number;
  payment_method_id?: number | null;
  fee?: number;
}

type Editability = {
  allowed: boolean;
  reasons: string[];
  editable_until?: string | null;
  now?: string | null;
  window_seconds_remaining?: number | null;
  pro_override?: boolean;
};

/* =========================
   Utils
   ========================= */
const isStripeOnline = (pm?: PaymentMethod | null) => {
  if (!pm) return false;

  const provider = String(pm.provider || "")
    .toLowerCase()
    .trim();

  return pm.online === true && provider === "stripe";
};

function money(n: number | undefined | null) {
  if (typeof n !== "number" || Number.isNaN(n)) return "—";
  return formatMoney(n);
}

// Avoid Date("YYYY-MM-DD") UTC shift; use dateUtils local parsing
const ymdToTime = (ymd: string) => {
  const d = parseYmdLocal(ymd);
  return d ? d.getTime() : new Date(ymd).getTime();
};

// helpers (near other utils)


function pmWeight(pm: PaymentMethod, accounts: Account[]) {
  const name = (pm.name || "").toLowerCase();
  const type = (pm.type || "").toLowerCase();

  if (name.includes("cash")) return 10;

  if (
    name.includes("fps") ||
    name.includes("bank") ||
    name.includes("transfer") ||
    name.includes("payme")
  )
    return 20;

  if (
    name.includes("visa") ||
    name.includes("master") ||
    name.includes("mastercard") ||
    name.includes("amex") ||
    name.includes("american express") ||
    name.includes("card")
  )
    return 30;

  if (name.includes("wechat")) return 40;
  if (name.includes("alipay")) return 50;

  if (isDeferredSettlement(pm.type)) return 90;

  return 80;
}

function calcSubtotal(line: SaleLine) {
  return typeof line.total === "number" ? line.total : 0;
}

function calcFee(amt: number, feeRate = 0, fixedFee = 0) {
  if (!amt) return Math.round((fixedFee || 0) * 100) / 100;
  const percent = amt * (feeRate || 0);
  return Math.round((percent + (fixedFee || 0)) * 100) / 100;
}

type BubbleInfo = {
  label: string;
  bubbleType: BubbleType;
};

function getDetailBubbles(sale: Sale | null, totalPaid: number): BubbleInfo[] {
  if (!sale) return [];

  const isPreorder = sale.lines.some((l) => l.preorder) && !sale.voided;
  const isFulfilled =
    isPreorder && sale.lines.some((l) => l.preorder && l.fulfilled_date);
  const isPaid =
    sale.status?.toLowerCase() === "paid" ||
    sale.status?.toLowerCase() === "closed" ||
    Math.abs(totalPaid - sale.amount) < 0.005;
  const isPartial = totalPaid > 0 && !isPaid;

  if (sale.voided)
    return [
      {
        label: "Voided",
        bubbleType: "voided",
      },
    ];

  let out: BubbleInfo[] = [];
  if (!isPreorder) {
    if (isPaid)
      out = [
        {
          label: "Paid",
          bubbleType: "paid",
        },
      ];
    else if (isPartial)
      out = [
        {
          label: "Partial Paid",
          bubbleType: "partial",
        },
      ];
    else
      out = [
        {
          label: "Unpaid",
          bubbleType: "unpaid",
        },
      ];
  } else {
    if (!isFulfilled) {
      if (isPaid)
        out = [
          {
            label: "Preorder",
            bubbleType: "preorder",
          },
          {
            label: "Paid",
            bubbleType: "paid",
          },
        ];
      else if (isPartial)
        out = [
          {
            label: "Preorder",
            bubbleType: "preorder",
          },
          {
            label: "Partial Paid",
            bubbleType: "partial",
          },
        ];
      else
        out = [
          {
            label: "Preorder",
            bubbleType: "preorder",
          },
          {
            label: "Unpaid",
            bubbleType: "unpaid",
          },
        ];
    } else {
      if (isPaid)
        out = [
          {
            label: "Fulfilled",
            bubbleType: "fulfilled",
          },
          {
            label: "Paid",
            bubbleType: "paid",
          },
        ];
      else if (isPartial)
        out = [
          {
            label: "Fulfilled",
            bubbleType: "fulfilled",
          },
          {
            label: "Partial Paid",
            bubbleType: "partial",
          },
        ];
      else
        out = [
          {
            label: "Fulfilled",
            bubbleType: "fulfilled",
          },
          {
            label: "Unpaid",
            bubbleType: "unpaid",
          },
        ];
    }
  }
  return out;
}

/* =========================
   Local "sticky" paylink hint
   ========================= */
const LS_STICKY_KEY = (saleId: number | string) =>
  `rb_paylink_sticky_${saleId}`;
const STICKY_TTL_MS = 1000 * 60 * 60 * 48; // 48h

function setStickyPaylink(saleId: number | string) {
  const payload = { at: Date.now() };
  try {
    localStorage.setItem(LS_STICKY_KEY(saleId), JSON.stringify(payload));
  } catch {}
}
function clearStickyPaylink(saleId: number | string) {
  try {
    localStorage.removeItem(LS_STICKY_KEY(saleId));
  } catch {}
}
function hasFreshStickyPaylink(saleId: number | string) {
  try {
    const raw = localStorage.getItem(LS_STICKY_KEY(saleId));
    if (!raw) return false;
    const { at } = JSON.parse(raw || "{}");
    return typeof at === "number" && Date.now() - at < STICKY_TTL_MS;
  } catch {
    return false;
  }
}

/* =========================
   Component
   ========================= */
type LocalSalesMessage = { key: ParseKeys; values?: Record<string, string | number>; closedDate?: string | null };
type SalesMessage = string | LocalSalesMessage;
const localMessage = (key: ParseKeys, values?: Record<string, string | number>): LocalSalesMessage => ({ key, values });
class LocalSalesError extends Error {
  constructor(readonly display: LocalSalesMessage) { super(display.key); }
}
function localApiDetail(err: any): SalesMessage {
  if (err instanceof LocalSalesError) return err.display;
  const outcome = extractApiDetailOutcome(err);
  return outcome.kind === "raw" ? outcome.text : localMessage("sales.errors.generic");
}
function localClosedPeriod(closedDate?: string | null): LocalSalesMessage {
  return { key: "sales.errors.closedSubmit", closedDate };
}

export default function SaleDetail() {
  const { t } = useTranslation();
  const messageText = (message: SalesMessage | null): string => {
    if (message == null) return "";
    if (typeof message === "string") return message;
    const values = "closedDate" in message
      ? { date: message.closedDate ? formatClosedThroughDate(message.closedDate) : t("sales.errors.closedDate") }
      : message.values;
    return t(message.key, values ?? {});
  };
  const posthog = usePostHog();
  const navigate = useNavigate();
  const { id, company_id } = useParams<{
    id: string;
    company_id: string;
  }>();
  const location = useLocation();

  // main sale
  const [sale, setSale] = useState<Sale | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<SalesMessage | null>(null);
  const [closedThroughDate, setClosedThroughDate] = useState<string | null>(
    null,
  );

  // preorder fulfillment
  const [fulfilling, setFulfilling] = useState(false);
  const [fulfillError, setFulfillError] = useState<SalesMessage | null>(null);

  // payment recording UI
  const [paying, setPaying] = useState(false);
  const [paymentDate, setPaymentDate] = useState(() => ymdTodayLocal());
  const [paymentError, setPaymentError] = useState<SalesMessage | null>(null);
  const [paymentAmount, setPaymentAmount] = useState<string>("");
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [switchingPaymentMethod, setSwitchingPaymentMethod] = useState(false);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<
    number | null
  >(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [customerPayments, setCustomerPayments] = useState<CustomerPayment[]>(
    [],
  );
  // Customer Payment attempt-state machine (idle | in-flight | ambiguous),
  // scoped to the currently-resolved invoice_id. Replaces the old bare
  // `offlinePayIdemRef` -- see utils/customerPaymentIdempotency.ts. A
  // separate synchronous boolean ref guards against two immediate
  // invocations (e.g. a double click or repeated tap before React
  // re-renders the disabled button) -- `paying` state alone can't catch
  // this since it only updates on the next render. Mirrors
  // FixedAssetDetail.tsx's savePayment / PurchaseDetail.tsx's
  // handleAddPayment. `paying` itself remains purely presentational
  // (button label/disabled) and is always toggled in lockstep with this
  // ref, at the same two call sites, so the two can never drift: every
  // exit from handleMarkPaid's try/catch reassigns
  // customerPaymentAttemptRef.current to a non-"in-flight" state BEFORE
  // the `finally` block runs `setPaying(false)`.
  const customerPaymentAttemptRef = useRef<CustomerPaymentAttempt>(
    IDLE_CUSTOMER_PAYMENT_ATTEMPT,
  );
  const customerPaymentInFlightRef = useRef(false);
  const invoiceSendWindowRef = useRef<Window | null>(null);
  useEffect(() => {
    // Update only the owned, still-local loading shell; never navigate or resend.
    try {
      const popup = invoiceSendWindowRef.current;
      if (!popup || popup.closed) return;
      const title = popup.document.getElementById("nimos-sendoff-title");
      const detail = popup.document.getElementById("nimos-sendoff-detail");
      if (title) {
        title.textContent = t("sales.detail.preparingInvoice");
        popup.document.title = t("sales.detail.preparingInvoice");
      }
      if (detail) detail.textContent = t("sales.detail.continuingDelivery");
    } catch { /* An external destination owns its content after navigation. */ }
  }, [t]);

  const closeInvoiceSendWindow = () => {
    try {
      const w = invoiceSendWindowRef.current;
      if (w && !w.closed) w.close();
    } catch {}
    invoiceSendWindowRef.current = null;
  };

  const prepareInvoiceSendWindow = () => {
    try {
      const existing = invoiceSendWindowRef.current;
      if (existing && !existing.closed) return existing;

      const w = window.open(
        "",
        "nimos_invoice_sendoff",
        "popup=yes,width=520,height=720,left=120,top=80",
      );
      if (!w) return null;

      try {
        w.document.title = t("sales.detail.preparingInvoice");
        w.document.body.innerHTML = `
        <div style="
          min-height:100vh;
          margin:0;
          display:flex;
          align-items:center;
          justify-content:center;
          background:#f9fafb;
          font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;
          color:#374151;
        ">
          <div style="display:flex;flex-direction:column;align-items:center;gap:14px;padding:24px;">
            <div style="
              width:28px;
              height:28px;
              border:3px solid #d1d5db;
              border-top-color:#2563eb;
              border-radius:9999px;
              animation:nimos-spin 0.8s linear infinite;
            "></div>
            <div id="nimos-sendoff-title" style="font-size:15px;font-weight:600;"></div>
            <div id="nimos-sendoff-detail" style="font-size:13px;color:#6b7280;"></div>
          </div>
        </div>
        <style>
          html, body { margin: 0; padding: 0; }
          @keyframes nimos-spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        </style>
      `;
        const title = w.document.getElementById("nimos-sendoff-title");
        const detail = w.document.getElementById("nimos-sendoff-detail");
        if (title) title.textContent = t("sales.detail.preparingInvoice");
        if (detail) detail.textContent = t("sales.detail.continuingDelivery");
      } catch {}

      invoiceSendWindowRef.current = w;
      return w;
    } catch {
      return null;
    }
  };

  // fee override UI
  const [showFeeOverride, setShowFeeOverride] = useState(false);
  const [feeOverride, setFeeOverride] = useState<string>("");

  // memo edit UI
  const [editingMemo, setEditingMemo] = useState(false);
  const [memoDraft, setMemoDraft] = useState("");
  const [memoSaving, setMemoSaving] = useState(false);
  const [memoError, setMemoError] = useState<SalesMessage | null>(null);

  // delete modal
  const [showDelete, setShowDelete] = useState(false);
  const [deleteError, setDeleteError] = useState<SalesMessage | null>(null);

  // editability window from backend
  const [ed, setEd] = useState<Editability | null>(null);

  // blocked edit modal
  const [showBlockedEdit, setShowBlockedEdit] = useState(false);
  const [blockedReasons, setBlockedReasons] = useState<SalesMessage[]>([]);

  // auth / current user
  const [me, setMe] = useState<AuthMe | null>(null);

  // stripe QRModel
  const [showStripeQrModal, setShowStripeQrModal] = useState(false);
  const [stripeCheckoutUrl, setStripeCheckoutUrl] = useState("");
  const [copiedCheckoutUrl, setCopiedCheckoutUrl] = useState(false);

  // pay link modal
  const [showPayLinkModal, setShowPayLinkModal] = useState(false);
  const [payLinkUrl, setPayLinkUrl] = useState<string | null>(null);

  // invoice send state
  const [sendingInvoice, setSendingInvoice] = useState(false);
  const [sendInvoiceError, setSendInvoiceError] = useState<SalesMessage | null>(null);
  const [sendInvoiceSuccess, setSendInvoiceSuccess] = useState<SalesMessage | null>(
    null,
  );
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [sendAfterCustomerSave, setSendAfterCustomerSave] = useState(false);
  const [customerSaving, setCustomerSaving] = useState(false);
  const [customerError, setCustomerError] = useState<SalesMessage | null>(null);
  const [customerNameDraft, setCustomerNameDraft] = useState("");
  const [customerEmailDraft, setCustomerEmailDraft] = useState("");
  const [customerMobileDraft, setCustomerMobileDraft] = useState("");
  // copy/paylink UX feedback
  const [copiedPaylink, setCopiedPaylink] = useState(false);

  const invoiceProviderMeta = sale?.invoice_provider_meta || {};

  const hasStripeConflict =
    Boolean(sale?.stripe_conflict) ||
    Boolean(sale?.reconcile_required) ||
    sale?.provider_status === "conflict" ||
    sale?.invoice_provider_status === "conflict" ||
    Boolean(invoiceProviderMeta?.reconcile_required) ||
    invoiceProviderMeta?.reconcile_reason ===
      "late_stripe_success_after_offline_settlement";

  /* -------------------------
     Data refresh helpers
     ------------------------- */
  const refreshSaleOnly = useCallback(async () => {
    if (!id || !company_id) return null;

    const res = await apiGet<Sale>(
      `/sales/${id}?company_id=${company_id}&_=${Date.now()}`,
    );

    setSale(res);

    // Normalize URL to canonical sale.id
    if (res?.id != null && String(res.id) !== String(id)) {
      navigate(companyRoute(company_id, `/sales/${res.id}`), { replace: true });
    }

    return res;
  }, [id, company_id, navigate]);

  const refreshSaleAndPayments = useCallback(async () => {
    const updated = await refreshSaleOnly();
    if (!updated) return;

    // Updating sale triggers the independent history effect. Receipt rows are
    // optional display data, never a prerequisite for authoritative totals.
    const e = await apiGet<Editability>(
      `/sales/${updated.id}/editability?company_id=${company_id}`,
    ).catch(() => null);
    if (e) setEd(e);

    // clear sticky hint if fully paid / voided
    const totalPaidNow = Number(updated.amount_paid || 0);
    const isPaidNow =
      updated.status?.toLowerCase() === "paid" ||
      updated.status?.toLowerCase() === "closed" ||
      Math.abs(totalPaidNow - updated.amount) < 0.005;

    if (isPaidNow || updated.voided) {
      clearStickyPaylink(updated.id);
    }
  }, [company_id, refreshSaleOnly]);

  /* -------------------------
     Effects
     ------------------------- */
  // who am I
  useEffect(() => {
    apiGet<AuthMe>("/auth/me")
      .then(setMe)
      .catch(() => setMe(null));
  }, []);

  // Stripe return finalization
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const stripeFlag = params.get("stripe");
    const cs = params.get("session_id") || params.get("cs");

    let invoiceId: number | null =
      Number(params.get("invoice_id") || "0") || null;

    if ((stripeFlag === "1" || stripeFlag === "success") && cs && company_id) {
      (async () => {
        try {
          const s = sale ?? (await refreshSaleOnly());
          const saleId = s?.id ?? id;
          const refreshedInvoiceId = Number(
            s?.invoice_id ?? sale?.invoice_id ?? 0,
          );
          invoiceId = invoiceId ?? (refreshedInvoiceId || null);

          if (saleId) {
            await apiPost(
              `/stripe/sales/${saleId}/gateway/finalize-from-session?company_id=${company_id}`,
              { cs },
            );
          }
        } catch {}

        if (!invoiceId && cs) {
          try {
            const resolved = await apiGet<{ invoice_id?: number }>(
              `/payments/session/${encodeURIComponent(cs)}/resolve`,
            );
            invoiceId = resolved?.invoice_id ?? null;
          } catch {}
        }

        if (invoiceId) {
          try {
            for (let i = 0; i < 3; i++) {
              const res = await apiPost(
                `/payments/invoices/${invoiceId}/finalize`,
                {},
              );
              if (res?.ready || res?.ok) break;
              await new Promise((resolve) => setTimeout(resolve, 800));
            }
          } catch {}
        }

        window.history.replaceState({}, "", location.pathname);
        await refreshSaleAndPayments();
      })();
    }
  }, [
    location.search,
    location.pathname,
    company_id,
    id,
    sale,
    refreshSaleOnly,
    refreshSaleAndPayments,
  ]);

  // refetch on focus/visibility (unless PayLink modal is open)
  useEffect(() => {
    if (showPayLinkModal) return;
    const onFocus = () => refreshSaleAndPayments();
    const onVis = () => {
      if (document.visibilityState === "visible") {
        refreshSaleAndPayments();
      }
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [showPayLinkModal, refreshSaleAndPayments]);

  // initial load
  useEffect(() => {
    setLoading(true);
    setError(null);
    refreshSaleOnly()
      .catch(() => setError(localMessage("sales.errors.loadSale")))
      .finally(() => setLoading(false));
  }, [refreshSaleOnly]);

  useEffect(() => {
    if (!company_id) return;

    apiGet<{
      closed_through_date?: string | null;
    }>(`/settings/financial-year-end?company_id=${company_id}`)
      .then((res) => {
        setClosedThroughDate(res?.closed_through_date || null);
      })
      .catch(() => {
        setClosedThroughDate(null);
      });
  }, [company_id]);

  // load all payment methods
  useEffect(() => {
    apiGet<PaymentMethod[]>(`/payment-methods?company_id=${company_id}`)
      .then(setPaymentMethods)
      .catch(() => setPaymentMethods([]));
  }, [company_id]);

  // pick initial payment method based on sale
  useEffect(() => {
    if (!sale || paymentMethods.length === 0) return;
    const pmId = sale.payment_method?.id ?? sale.payment_method_id ?? null;
    if (pmId && paymentMethods.some((pm) => pm.id === pmId)) {
      setSelectedPaymentMethodId(pmId);
    }
  }, [sale, paymentMethods]);

  // load accounts
  useEffect(() => {
    apiGet<Account[]>(`/accounts?company_id=${company_id}`)
      .then(setAccounts)
      .catch(() => setAccounts([]));
  }, [company_id]);

  // History supplies rows only; the sale response owns payment totals/status.
  useEffect(() => {
    if (!sale) return;
    let active = true;
    setCustomerPayments([]);
    apiGet<CustomerPayment[]>(
      `/ar-ap/customer-payments?company_id=${company_id}`,
    ).then((payments) => {
      const salePayments = payments
        .filter((p) => p.invoice_id == sale.invoice_id)
        .sort((a, b) => ymdToTime(a.payment_date) - ymdToTime(b.payment_date));
      if (active) setCustomerPayments(salePayments);
    }).catch(() => {
      if (active) setCustomerPayments([]);
    });
    return () => { active = false; };
  }, [sale, company_id]);

  // hydrate local modal url from server
  useEffect(() => {
    if (sale?.paylink_url) {
      setPayLinkUrl(sale.paylink_url);
    } else {
      setPayLinkUrl(null);
    }
  }, [sale?.paylink_url]);

  /* -------------------------
     Derived
     ------------------------- */
  const selectedPM = useMemo(
    () =>
      paymentMethods.find((pm) => pm.id === selectedPaymentMethodId) || null,
    [paymentMethods, selectedPaymentMethodId],
  );

  const selectedIsStripe = isStripeOnline(selectedPM);

  const isClosedPaymentDate = useMemo(() => {
    return isDateClosed(paymentDate, closedThroughDate);
  }, [paymentDate, closedThroughDate]);

  // Pending Stripe sale = still on Stripe, no recorded payment yet,
  // not voided, not preorder, and not a paylink.
  // Use only fields already available here so we avoid declaration-order issues.
  const isPendingGatewaySale = useMemo(() => {
    if (!sale) return false;

    const status = String(sale.status || "")
      .toLowerCase()
      .trim();
    const statusRaw = String(sale.status_raw || "")
      .toLowerCase()
      .trim();
    const paymentStatus = String(sale.payment_status || "")
      .toLowerCase()
      .trim();

    const hasStripePM =
      !!sale.payment_method && isStripeOnline(sale.payment_method);

    const isPreorder = !!sale.lines?.some((l) => !!l.preorder);

    const isFullyPaid =
      status === "paid" ||
      status === "closed" ||
      Math.abs((sale.amount_paid || 0) - (sale.amount || 0)) < 0.005;

    const isGatewayInFlight =
      status === "pending_payment" ||
      status === "partial" ||
      statusRaw === "pending_payment" ||
      statusRaw === "partial" ||
      paymentStatus === "pending_payment" ||
      paymentStatus === "partial";

    return (
      hasStripePM &&
      !sale.voided &&
      !isPreorder &&
      !sale.via_paylink &&
      !sale.paylink_url &&
      !isFullyPaid &&
      isGatewayInFlight
    );
  }, [sale]);

  // visible list of payment methods: hide On Account, sort offline methods, append one Stripe method last
  const visiblePaymentMethods = useMemo<PaymentMethod[]>(() => {
    const base = paymentMethods.filter(
      (pm: PaymentMethod) => !isDeferredSettlement(pm.type),
    );

    const stripeMethods = base.filter((pm) => isStripeOnline(pm));
    const stripeDefault = stripeMethods.length ? stripeMethods[0] : null;

    const nonStripe = base
      .filter((pm) => !isStripeOnline(pm))
      .slice()
      .sort(
        (a, b) =>
          pmWeight(a, accounts) - pmWeight(b, accounts) ||
          a.name.localeCompare(b.name),
      );

    return stripeDefault ? [...nonStripe, stripeDefault] : nonStripe;
  }, [paymentMethods, accounts]);

  const autoPayAmountRef = useRef<{ active: boolean; last: number }>({
    active: false,
    last: 0,
  });

  useEffect(() => {
    autoPayAmountRef.current = { active: false, last: 0 };
    setPaymentAmount("");
  }, [sale?.id]);

  // Restore (or reset) the Customer Payment attempt slot whenever the
  // resolved company/invoice changes -- covers both the initial mount/
  // reload case (sessionStorage may hold an ambiguous attempt from before
  // the reload) and in-app navigation between two different Sales: this
  // route (`sales/:id`) reuses the same mounted SaleDetail instance across
  // a `:id` param change (no remount), so without this effect a retained
  // in-memory attempt for Sale A's invoice would otherwise still be sitting
  // in customerPaymentAttemptRef when the user navigates to Sale B -- never
  // carrying an attempt from one invoice into another.
  useEffect(() => {
    const companyIdNum = Number(company_id);
    const invoiceId =
      sale?.invoice_id != null ? Number(sale.invoice_id) : null;
    if (!Number.isFinite(companyIdNum) || invoiceId == null || !Number.isFinite(invoiceId)) {
      customerPaymentAttemptRef.current = IDLE_CUSTOMER_PAYMENT_ATTEMPT;
      return;
    }
    const stored = readStoredCustomerPaymentAttempt(companyIdNum, invoiceId);
    customerPaymentAttemptRef.current = stored
      ? attemptFromStored(stored)
      : IDLE_CUSTOMER_PAYMENT_ATTEMPT;
  }, [company_id, sale?.invoice_id]);

  // auto-select first visible method if current is invalid
  useEffect(() => {
    if (visiblePaymentMethods.length === 0) {
      if (selectedPaymentMethodId !== null) setSelectedPaymentMethodId(null);
      return;
    }
    if (
      selectedPaymentMethodId == null ||
      !visiblePaymentMethods.some((pm) => pm.id === selectedPaymentMethodId)
    ) {
      setSelectedPaymentMethodId(visiblePaymentMethods[0].id);
    }
  }, [visiblePaymentMethods, selectedPaymentMethodId]);

  // FEE-RATE-SEMANTICS-1 Phase 2B: shared exact cash classifier.
  const selectedIsCash = isCashPaymentMethod(selectedPM);

  // Non-authoritative fee preview for offline methods only. Reads the derived
  // percent (fraction = %/100, exactly once); cash ignores rate AND fixed fee.
  const autoFee = useMemo(() => {
    if (!selectedPM || selectedIsStripe || selectedIsCash) return 0;
    const amt = parseFloat(paymentAmount || "0") || 0;
    const feeRate = paymentMethodFeeFraction(selectedPM);
    const fixedFee = paymentMethodFixedFee(selectedPM);
    return calcFee(amt, feeRate, fixedFee);
  }, [paymentAmount, selectedPM, selectedIsStripe, selectedIsCash]);

  const totalPaid = Number(sale?.amount_paid || 0);

  const isPaid =
    !!sale &&
    (sale.status?.toLowerCase() === "paid" ||
      sale.status?.toLowerCase() === "closed" ||
      Math.abs(totalPaid - sale.amount) < 0.005);

  const outstanding = sale ? Math.max(0, sale.amount - totalPaid) : 0;

  const hasPreOrderItem = sale?.lines.some((l) => l.preorder) && !sale?.voided;

  const isPro = !!me?.is_user_pro;
  const customerEmail = (sale?.customer?.email || "").trim();

  const customerMobile = useMemo(() => {
    const c = sale?.customer;
    return (
      [c?.mobile, c?.phone, c?.whatsapp]
        .map((v) => String(v || "").trim())
        .find(Boolean) || ""
    );
  }, [sale?.customer]);

  const hasCustomerEmail = !!customerEmail;
  const hasCustomerMobile = !!customerMobile;
  const isGuestCustomer =
    !!sale?.customer?.is_guest ||
    !sale?.customer?.id ||
    String(sale?.customer?.name || "")
      .trim()
      .toLowerCase() === "guest";

  const missingCustomerForSend =
    isGuestCustomer || (!hasCustomerEmail && !hasCustomerMobile);
  // PayLink detection (server + sticky local + optimistic modal/url state)
  const stickySaysPaylink = sale ? hasFreshStickyPaylink(sale.id) : false;

  const payLinkStatus = useMemo(() => {
    if (!sale) return null as string | null;

    const gatewayMethod = String(sale.gateway_method || "")
      .toLowerCase()
      .trim();

    const paymentStatus = String(sale.payment_status || "")
      .toLowerCase()
      .trim();

    const statusRaw = String(sale.status_raw || "")
      .toLowerCase()
      .trim();

    const hasPayLinkMarker =
      !!sale.via_paylink ||
      !!sale.paylink_url ||
      gatewayMethod === "paylink" ||
      stickySaysPaylink ||
      !!payLinkUrl ||
      showPayLinkModal;

    if (!hasPayLinkMarker) return null;

    const raw = paymentStatus || statusRaw;

    if (
      isPaid ||
      raw === "paid" ||
      raw === "closed" ||
      raw === "completed" ||
      raw === "succeeded"
    ) {
      return "paid";
    }

    if (
      raw === "expired" ||
      raw === "revoked" ||
      raw === "cancelled" ||
      raw === "canceled" ||
      raw === "voided"
    ) {
      return "expired";
    }

    if (
      raw === "open" ||
      raw === "opened" ||
      raw === "sent" ||
      raw === "created" ||
      raw === "draft" ||
      raw === "pending" ||
      raw === "pending_payment" ||
      raw === "requires_payment_method"
    ) {
      return raw;
    }

    return "sent";
  }, [sale, stickySaysPaylink, payLinkUrl, showPayLinkModal, isPaid]);

  const payLinkPending =
    !!payLinkStatus && payLinkStatus !== "paid" && payLinkStatus !== "expired";

  // default payment amount to current outstanding balance
  useEffect(() => {
    if (!sale) return;
    if (sale.voided) return;
    if (isPaid) return;
    if (payLinkPending) return;

    const raw = (paymentAmount || "").trim();
    const current = raw === "" ? NaN : parseFloat(raw);
    const currentNum = Number.isFinite(current) ? current : 0;

    const wasAuto =
      autoPayAmountRef.current.active &&
      Math.abs(currentNum - autoPayAmountRef.current.last) < 0.005;

    const userHasTyped = raw !== "" && !wasAuto;

    if (!userHasTyped && outstanding > 0) {
      setPaymentAmount(
        outstanding > 0 ? String(Number(outstanding.toFixed(2))) : "",
      );
      autoPayAmountRef.current = { active: true, last: outstanding };
    }
  }, [sale, isPaid, payLinkPending, outstanding, paymentAmount]);

  const bubbles = getDetailBubbles(sale, totalPaid);

  const headerDueDate =
    !!sale?.on_account && sale?.due_date?.trim()
      ? sale.due_date!.trim()
      : undefined;

  const hasPayment = totalPaid > 0;

  // edit lock logic
  const baseEditable = !!sale && !sale.voided && !isPaid && !hasPayment;
  const windowAllows = ed?.allowed ?? true;
  const proOverrideActive = !!me?.is_user_pro && ed?.pro_override === true;

  const canEdit = baseEditable && (windowAllows || proOverrideActive);

  const editDisallowedReason = !baseEditable
    ? sale?.voided
      ? localMessage("sales.errors.voided")
      : isPaid
        ? localMessage("sales.errors.editPaid")
        : hasPayment
          ? localMessage("sales.errors.editReceived")
          : ""
    : !windowAllows && !proOverrideActive
      ? ed?.reasons?.[0] || localMessage("sales.errors.editingLocked")
      : "";

  const canSendInvoice = !!sale?.invoice_id && !sale?.voided && isPro;

  const hasPayLinkAction =
    !!(payLinkUrl || sale?.paylink_url) && !sale?.voided && !isPaid;

  function openCustomerModal(sendAfterSave: boolean) {
    setSendAfterCustomerSave(sendAfterSave);
    setCustomerNameDraft(isGuestCustomer ? "" : sale?.customer?.name || "");
    setCustomerEmailDraft(sale?.customer?.email || "");
    setCustomerMobileDraft(customerMobile || "");
    setCustomerError(null);
    setShowCustomerModal(true);
  }

  const handleSendInvoiceClick = async () => {
    if (sendingInvoice) return;
    if (showCustomerModal) return;
    if (!sale?.invoice_id || sale?.voided) return;

    // 🔴 BASIC USER → BLOCKED
    if (!isPro) {
      posthog.capture("invoice_manual_send_blocked", {
        sale_id: sale.id,
        invoice_id: sale.invoice_id,
        company_id,
        source: "sale_detail",
        trigger_type: "manual",
        user_plan: "basic",
        block_reason: "pro_required",
        entry_point: "airplane_button",
      });

      posthog.capture("pro_upgrade_clicked", {
        company_id,
        source: "sale_detail",
        trigger_reason: "invoice_manual_send",
        user_plan: "basic",
        entry_point: "airplane_button",
      });

      setShowUpgrade(true);
      return;
    }

    // 🟢 PRO USER → INTENT
    posthog.capture("invoice_manual_send_clicked", {
      sale_id: sale.id,
      invoice_id: sale.invoice_id,
      company_id,
      source: "sale_detail",
      trigger_type: "manual",
      user_plan: "pro",
      has_customer_email: hasCustomerEmail,
      has_customer_mobile: hasCustomerMobile,
    });

    if (missingCustomerForSend) {
      openCustomerModal(true);
      return;
    }
    // Pre-open window (existing logic)
    prepareInvoiceSendWindow();

    await handleSendInvoiceBySettings();
  };

  /* -------------------------
     Keep server & UI in sync helpers
     ------------------------- */
  async function ensureStripePMForPayLink(stripePMId: number) {
    if (!sale) return;

    const currentId = sale.payment_method?.id ?? sale.payment_method_id ?? null;
    if (currentId === stripePMId) return;

    try {
      await apiPost(
        `/sales/${sale.id}/gateway/switch-to-paylink?company_id=${company_id}`,
        { payment_method_id: stripePMId },
      );
    } catch (error: any) {
      // Keep the earlier tender intact. The collection endpoint decides whether
      // a remaining balance can be linked, or returns the bilingual paid refusal.
      if (error?.response?.status === 400 &&
          error?.response?.data?.detail === "Sale already has payments") return;
      throw error;
    }

    await refreshSaleAndPayments();
  }

  async function convertPendingGatewayToOffline(
    newPaymentMethodId: number,
    opts?: { on_account?: boolean; due_date?: string | null },
  ) {
    if (!sale) return;

    await apiPost(
      `/sales/${sale.id}/gateway/cancel-convert?company_id=${company_id}`,
      {
        new_payment_method_id: newPaymentMethodId,
        on_account: !!opts?.on_account,
        due_date: opts?.on_account ? opts?.due_date || undefined : undefined,
      },
    );

    clearStickyPaylink(sale.id);
    await refreshSaleAndPayments();
  }

  async function handlePaymentMethodChange(nextId: number | null) {
    setSelectedPaymentMethodId(nextId);
    setPaymentError(null);

    if (!sale || !nextId) return;

    const nextMethod = paymentMethods.find((pm) => pm.id === nextId) || null;
    if (!nextMethod) return;

    // Auto-convert only when sale is still an in-flight Stripe/gateway sale
    // and user switches to a non-Stripe method.
    if (isPendingGatewaySale && !isStripeOnline(nextMethod)) {
      setSwitchingPaymentMethod(true);
      try {
        await convertPendingGatewayToOffline(nextMethod.id, {
          on_account: isDeferredSettlement(nextMethod.type),
          due_date: isDeferredSettlement(nextMethod.type)
            ? sale.due_date || null
            : null,
        });

        const refreshed = await refreshSaleOnly();
        if (refreshed) {
          setSelectedPaymentMethodId(
            refreshed.payment_method?.id ??
              refreshed.payment_method_id ??
              nextMethod.id,
          );
        } else {
          setSelectedPaymentMethodId(nextMethod.id);
        }
      } catch (e: any) {
        const detail = localApiDetail(e) || localMessage("sales.errors.convertStripe");

        setPaymentError(detail);

        const refreshed = await refreshSaleOnly().catch(() => null);
        setSelectedPaymentMethodId(
          refreshed?.payment_method?.id ??
            refreshed?.payment_method_id ??
            sale.payment_method?.id ??
            sale.payment_method_id ??
            null,
        );
      } finally {
        setSwitchingPaymentMethod(false);
      }
      return;
    }
  }

  async function addServerPaylinkTag() {
    if (!sale) return;
    try {
      const nextTags = Array.from(new Set([...(sale.tags || []), "paylink"]));
      await apiPatch(`/sales/${sale.id}?company_id=${company_id}`, {
        tags: nextTags,
      });
      setSale((prev) =>
        prev
          ? {
              ...prev,
              tags: nextTags,
            }
          : prev,
      );
    } catch {}
  }

  async function removeServerPaylinkTag() {
    if (!sale) return;
    try {
      const nextTags = (sale.tags || []).filter(
        (t) => (t || "").toLowerCase() !== "paylink",
      );

      await apiPatch(`/sales/${sale.id}?company_id=${company_id}`, {
        tags: nextTags,
      });

      setSale((prev) =>
        prev
          ? {
              ...prev,
              tags: nextTags,
            }
          : prev,
      );
    } catch {}
  }

  async function handleSaveCustomerDetails() {
    if (!sale) return;

    const name = customerNameDraft.trim();
    const email = customerEmailDraft.trim();
    const mobile = customerMobileDraft.trim();

    if (!name) {
      setCustomerError(localMessage("directory.customers.nameRequired"));
      return;
    }

    if (sendAfterCustomerSave && !email && !mobile) {
      setCustomerError(localMessage("sales.errors.customerContactRequired"));
      return;
    }
    if (sendAfterCustomerSave) {
      prepareInvoiceSendWindow();
    }
    setCustomerSaving(true);
    setCustomerError(null);

    try {
      let customer: any;

      if (!isGuestCustomer && sale.customer?.id) {
        customer = await apiPut<any>(
          `/customers/${sale.customer.id}?company_id=${company_id}`,
          {
            name,
            email: email || null,
            mobile: mobile || null,
          },
        );
      } else {
        customer = await apiPost<any>(`/customers?company_id=${company_id}`, {
          name,
          email: email || null,
          mobile: mobile || null,
        });

        await apiPatch(`/sales/${sale.id}/customer?company_id=${company_id}`, {
          customer_id: customer.id,
        });
      }

      await refreshSaleAndPayments();
      setShowCustomerModal(false);

      if (sendAfterCustomerSave) {
        setSendAfterCustomerSave(false);
        await handleSendInvoiceBySettings();
      }
    } catch (e: any) {
      setCustomerError(
        e?.response?.data?.detail ||
          (e instanceof LocalSalesError ? e.display : e?.message) ||
          localMessage("sales.errors.saveCustomer"),
      );
    } finally {
      setCustomerSaving(false);
    }
  }

  /* -------------------------
     Actions
     ------------------------- */

  async function copyTextSafe(str: string): Promise<boolean> {
    try {
      await navigator.clipboard.writeText(str);
      return true;
    } catch {
      try {
        const ta = document.createElement("textarea");
        ta.value = str;
        ta.style.position = "fixed";
        ta.style.top = "-1000px";
        ta.style.left = "-1000px";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(ta);
        return ok;
      } catch (err2) {
        console.error("copyTextSafe failed", err2);
        return false;
      }
    }
  }

  // preorder fulfill
  const handleFulfillPreOrder = async () => {
    if (!sale) return;
    setFulfilling(true);
    setFulfillError(null);
    try {
      await apiPost(`/sales/${sale.id}/fulfill?company_id=${company_id}`);
      await refreshSaleAndPayments();
    } catch (e: any) {
      if (isClosedPeriodError(e)) {
        setFulfillError(localMessage("sales.errors.closedEdit"));
        return;
      }

      setFulfillError(
        localApiDetail(e) || localMessage("sales.errors.fulfill"),
      );
    } finally {
      setFulfilling(false);
    }
  };

  // offline Add Payment
  const handleMarkPaid = async () => {
    if (switchingPaymentMethod) {
      setPaymentError(localMessage("sales.errors.waitForMethod"));
      return;
    }
    if (!sale) return;
    // Synchronous guard against two immediate invocations (e.g. a double
    // click or repeated tap before React re-renders the disabled button)
    // resulting in two POSTs -- `paying` state alone can't catch this since
    // it only updates on the next render. Mirrors FixedAssetDetail.tsx's
    // savePayment / PurchaseDetail.tsx's handleAddPayment. Independent of
    // the attempt state machine below.
    if (customerPaymentInFlightRef.current) return;

    setPaymentError(null);

    const method = selectedPM;
    if (!method || !method.account_id) {
      setPaymentError(localMessage("sales.errors.unlinkedAccount"));
      return;
    }
    if (isStripeOnline(method)) {
      setPaymentError(localMessage("sales.errors.useStripe"));
      return;
    }

    const payAmt = parseFloat(paymentAmount || "0");
    if (!payAmt || payAmt <= 0) {
      setPaymentError(localMessage("sales.errors.enterAmount"));
      return;
    }
    if (payAmt > outstanding + 0.005) {
      setPaymentError(localMessage("sales.errors.overpayment"));
      return;
    }

    // FEE-RATE-SEMANTICS-1 Phase 2B (Finding 3): resolve the posting fee intent
    // via the SHARED production builder (also unit-tested). Invalid non-blank
    // override blocks BEFORE any network call and never serializes NaN.
    const feeResolution = resolveCustomerPaymentFee({
      isCash: selectedIsCash,
      overrideShown: showFeeOverride,
      overrideRaw: feeOverride,
    });
    if (!feeResolution.ok) {
      setPaymentError(
        localMessage("sales.errors.invalidCollectionFee"),
      );
      return;
    }
    // Canonical fee-intent for the identity fingerprint. configured omits fee;
    // explicit (incl. cash 0) carries it.
    const feeCanonical =
      feeResolution.feeMode === "explicit"
        ? { mode: "explicit" as const, fee: feeResolution.fee }
        : { mode: "configured" as const };

    const companyIdNum = Number(company_id);
    // Set only once the decision below actually accepts a submission for a
    // resolved invoice_id -- used in the catch block to tell "failed before
    // ever deciding to submit a customer payment" (e.g. the pending-gateway
    // conversion step below) apart from "the customer-payments call itself
    // failed", so a failure unrelated to this endpoint can never write a
    // phantom ambiguous customer-payment attempt to memory or storage.
    let submittedInvoiceId: number | null = null;
    // Set to true the instant apiPost resolves and the attempt has already
    // been resolved to success (memory + storage). A failure in ANYTHING
    // after that point (refreshSaleAndPayments, etc.) is a post-success
    // cleanup failure, not a payment failure -- it must never reclassify
    // or touch the attempt again (in particular, it must never turn an
    // already-successful attempt back into "ambiguous").
    let paymentConfirmedSuccess = false;

    setPaying(true);
    try {
      const isPendingPayment = isPendingGatewaySale;
      let workingSale = sale;

      if (isPendingPayment) {
        await convertPendingGatewayToOffline(method.id, {
          on_account: isDeferredSettlement(method.type),
          due_date: isDeferredSettlement(method.type)
            ? sale.due_date || null
            : null,
        });

        const refreshed = await refreshSaleOnly();
        if (refreshed) workingSale = refreshed;
      }

      const invoiceId = Number(workingSale.invoice_id);
      if (!Number.isFinite(invoiceId)) {
        setPaymentError(localMessage("sales.errors.noInvoice"));
        return;
      }

      // Validation passed -- only now do we build the canonical payload and
      // ask the state machine whether this is a first attempt (mint a new
      // key), an unchanged retry of a still-ambiguous SAME-invoice attempt
      // (reuse its key), an edited retry of one (block, no network call),
      // or a retry against a DIFFERENT invoice while an ambiguous attempt
      // is still outstanding (block, no network call, no silent key
      // rotation). Fee participates in the fingerprint: the manual backend
      // endpoint this page calls treats fee as part of request identity
      // (7a086205), so a fee-only edit must be detected the same as any
      // other field edit.
      const canonicalPayload = canonicalCustomerPayment(
        invoiceId,
        paymentDate,
        payAmt,
        method.account_id,
        method.id,
        feeCanonical,
      );
      const fingerprint = fingerprintCustomerPayment(canonicalPayload);
      const decision = decideCustomerPaymentAttempt(
        customerPaymentAttemptRef.current,
        invoiceId,
        canonicalPayload,
        fingerprint,
        () => customerPaymentIdempotencyKey(invoiceId),
      );

      if (decision.kind === "blocked-mismatch") {
        setPaymentError(localMessage("sales.retry.paymentAmbiguous"));
        return;
      }
      if (decision.kind === "blocked-legacy-shape") {
        // FEE-RATE-SEMANTICS-1 Phase 2B (Condition 3): a pre-2B stored
        // fingerprint can never match a current-shape retry -- direct the user
        // to verify history rather than implying an unchanged retry resolves it.
        // The stored key/fingerprint are left untouched (never rebound/cleared).
        setPaymentError(localMessage("sales.retry.paymentLegacy"));
        return;
      }
      if (decision.kind === "blocked-cross-invoice") {
        setPaymentError(localMessage("sales.retry.paymentOtherInvoice"));
        return;
      }
      if (decision.kind === "blocked-in-flight") {
        // Unreachable in practice -- customerPaymentInFlightRef already
        // returned above before this point. Kept for correctness of the
        // pure decision function itself.
        setPaymentError(localMessage("sales.retry.paymentInFlight"));
        return;
      }

      submittedInvoiceId = invoiceId;
      customerPaymentAttemptRef.current = decision.attempt;
      customerPaymentInFlightRef.current = true;

      // Persist the ambiguous-shaped record BEFORE the request is sent --
      // a reload during or after this request must restore as "ambiguous",
      // never "in-flight" (nothing "in-flight"-shaped is ever written to
      // storage at all -- see StoredCustomerPaymentAttempt). The original
      // request may still be processing when the page reloads, or it may
      // already have completed successfully before restoration; the
      // browser cannot know which occurred, so restoring as ambiguous is
      // the only correct choice. An unchanged same-key retry after that
      // restore safely replays either the completed payment or the still-
      // resolving attempt; an edited retry is blocked because the
      // original outcome is unknown. The backend remains the source of
      // truth for whether the payment actually completed.
      writeStoredCustomerPaymentAttempt(companyIdNum, {
        status: "ambiguous",
        invoiceId,
        idempotencyKey: decision.idempotencyKey,
        fingerprint,
      });

      // Discriminated fee-intent request fields (shared helper): configured
      // omits fee (backend computes); explicit (incl. cash 0) carries a numeric
      // fee. Strict FeeIntent so a bad shape cannot compile.
      const feeRequestFields = customerPaymentFeeIntentFields(feeResolution);
      // The actual posted object, TYPED (Condition 4b) -- not an inline literal.
      const paymentBody: CustomerPaymentPostBody = {
        company_id,
        invoice_id: invoiceId,
        payment_date: paymentDate,
        amount: payAmt,
        payment_account_id: method.account_id,
        payment_method_id: method.id,
        ...feeRequestFields,
      };
      await apiPost("/ar-ap/customer-payments", paymentBody, {
        headers: { "Idempotency-Key": decision.idempotencyKey },
      });

      // Confirmed success (create or replay -- both return the same
      // response shape): clear the attempt (memory + storage) so the next
      // payment (e.g. a later distinct partial payment) gets a fresh key
      // instead of leaking this one into it. Set BEFORE any of the
      // following cleanup/refresh calls, which can themselves throw (e.g.
      // refreshSaleAndPayments is a real network call) -- once this line
      // has run, the payment is confirmed recorded and the attempt is
      // already correctly resolved; nothing below may ever touch it again.
      customerPaymentAttemptRef.current = resolveCustomerPaymentSuccess();
      clearStoredCustomerPaymentAttempt(companyIdNum, invoiceId);
      paymentConfirmedSuccess = true;

      setPaymentAmount("");
      setFeeOverride("");
      setShowFeeOverride(false);

      clearStickyPaylink(sale.id);

      await refreshSaleAndPayments();
    } catch (e: any) {
      if (paymentConfirmedSuccess) {
        // The payment itself already succeeded and the attempt was already
        // resolved/cleared above -- this is a failure in POST-success
        // cleanup (refreshSaleAndPayments), not a payment failure. It must
        // never be reclassified as one: never re-touch the attempt (in
        // particular, never turn an already-successful/idle attempt back
        // into "ambiguous"), and never show a message implying the
        // payment itself failed.
        setPaymentError(
          localMessage("sales.errors.paymentRecordedRefresh"),
        );
      } else if (submittedInvoiceId == null) {
        // Failed before ever reaching (or being accepted by) the customer-
        // payment decision gate -- e.g. convertPendingGatewayToOffline or
        // refreshSaleOnly. No customer-payment attempt state to update.
        if (isClosedPeriodError(e)) {
          setPaymentError(localClosedPeriod(closedThroughDate));
        } else {
          setPaymentError(localApiDetail(e) || localMessage("sales.errors.recordPayment"));
        }
      } else {
        const failureClass = classifyCustomerPaymentFailure(e, isClosedPeriodError);

        if (failureClass === "closed-period") {
          // Definite pre-commit rejection: assert_year_open raises before
          // any row is touched, so nothing was persisted. Clear the
          // attempt so a corrected date can retry under a new key.
          customerPaymentAttemptRef.current = resolveCustomerPaymentDefiniteRejection();
          clearStoredCustomerPaymentAttempt(companyIdNum, submittedInvoiceId);
          setPaymentError(localClosedPeriod(closedThroughDate));
        } else if (failureClass === "idempotency-conflict") {
          // Definite: this key is permanently bound to a different,
          // already-recorded payment. Retrying under it can never succeed
          // for this payload -- clear it so the next attempt mints a
          // fresh one.
          customerPaymentAttemptRef.current = resolveCustomerPaymentDefiniteRejection();
          clearStoredCustomerPaymentAttempt(companyIdNum, submittedInvoiceId);
          setPaymentError(localMessage("sales.retry.paymentConflict"));
        } else if (failureClass === "definite-rejection") {
          // HTTP 400/401/403/404/422 -- nothing persisted. Clear the
          // attempt so the user can edit and retry under a new key.
          customerPaymentAttemptRef.current = resolveCustomerPaymentDefiniteRejection();
          clearStoredCustomerPaymentAttempt(companyIdNum, submittedInvoiceId);
          setPaymentError(localApiDetail(e) || localMessage("sales.errors.recordPayment"));
        } else {
          // Ambiguous (network failure, timeout, unreadable response, HTTP
          // 500, or anything else unrecognized): retain the key,
          // fingerprint, and (already-written, see above) sessionStorage
          // record so an unchanged retry reuses the same key and safely
          // replays instead of risking a duplicate; an edited retry -- or
          // a retry against a different invoice -- stays blocked above.
          customerPaymentAttemptRef.current = resolveCustomerPaymentAmbiguousFailure(
            customerPaymentAttemptRef.current,
          );
          setPaymentError(localApiDetail(e) || localMessage("sales.errors.recordPayment"));
        }
      }
    } finally {
      customerPaymentInFlightRef.current = false;
      setPaying(false);
    }
  };

  // Stripe Checkout (charge now)
  const handleChargeWithStripe = async () => {
    if (!sale) return;
    setPaymentError(null);

    if (!selectedPM || !selectedIsStripe) {
      setPaymentError(localMessage("sales.errors.selectStripeCharge"));
      return;
    }

    const entered = parseFloat(paymentAmount || "0");
    const amountToCharge = entered > 0 ? entered : outstanding;

    if (!amountToCharge || amountToCharge <= 0) {
      setPaymentError(localMessage("sales.errors.nothingOutstanding"));
      return;
    }
    if (amountToCharge > outstanding + 0.005) {
      setPaymentError(localMessage("sales.errors.overpayment"));
      return;
    }

    try {
      const cents = Math.round(amountToCharge * 100);

      const res = await apiPost(
        `/stripe/sales/${sale.id}/checkout?company_id=${company_id}&return_to=detail`,
        {
          amount_cents: cents,
        },
      );

      const url = (res?.url ?? "") as string;
      if (!url) throw new LocalSalesError(localMessage("sales.errors.missingCheckoutUrl"));

      setStripeCheckoutUrl(url);
      setCopiedCheckoutUrl(false);
      setShowStripeQrModal(true);
    } catch (e: any) {
      setPaymentError(
        e?.response?.data?.detail ||
          (e instanceof LocalSalesError ? e.display : e?.message) ||
          localMessage("sales.errors.stripeCheckout"),
      );
    }
  };

  const closeStripeCheckout = async () => {
    setShowStripeQrModal(false);
    await refreshSaleOnly().catch(() => setError(localMessage("sales.errors.loadSale")));
  };

  const handleCopyCheckoutLink = async () => {
    if (!stripeCheckoutUrl) return;
    const ok = await copyTextSafe(stripeCheckoutUrl);
    if (ok) {
      setCopiedCheckoutUrl(true);
      window.setTimeout(() => setCopiedCheckoutUrl(false), 2000);
    }
  };

  // Create Pay Link (shareable checkout link)
  const handleGetPayLink = async () => {
    if (!sale) return;
    setPaymentError(null);

    if (!selectedPM || !selectedIsStripe) {
      setPaymentError(localMessage("sales.errors.selectStripeLink"));
      return;
    }

    const entered = parseFloat(paymentAmount || "0");
    const amountToCollect = entered > 0 ? entered : outstanding;

    if (!amountToCollect || amountToCollect <= 0) {
      setPaymentError(localMessage("sales.errors.nothingOutstanding"));
      return;
    }
    if (amountToCollect > outstanding + 0.005) {
      setPaymentError(localMessage("sales.errors.overpayment"));
      return;
    }

    try {
      await ensureStripePMForPayLink(selectedPM.id);

      const cents = Math.round(amountToCollect * 100);

      const res = await apiPost(
        `/stripe/sales/${sale.id}/paylink?company_id=${company_id}`,
        {
          amount_cents: cents,
        },
      );

      const url = (res?.url ?? "") as string;
      if (!url) throw new LocalSalesError(localMessage("sales.errors.missingPayLinkUrl"));

      // optional tag for compatibility with older UI elsewhere
      void addServerPaylinkTag();
      setStickyPaylink(sale.id);

      // optimistic local sale flags
      setSale((prev) =>
        prev
          ? {
              ...prev,
              via_paylink: true,
              via_gateway: true,
              paylink_url: url,
              status_raw: "pending_payment",
              payment_status: "pending_payment",
              gateway_provider: "stripe",
              gateway_method: "paylink",
            }
          : prev,
      );

      setPayLinkUrl(url);
      setShowPayLinkModal(true);

      const copiedNow = await copyTextSafe(url);
      if (copiedNow) {
        setCopiedPaylink(true);
        window.setTimeout(() => setCopiedPaylink(false), 2000);
      }
    } catch (e: any) {
      setPaymentError(
        e?.response?.data?.detail || (e instanceof LocalSalesError ? e.display : e?.message) || localMessage("sales.errors.createPayLink"),
      );
    }
  };

  const handlePayLinkDone = () => {
    setShowPayLinkModal(false);
  };

  const handleCancelPayLink = async () => {
    if (!sale) return;
    setPaymentError(null);

    try {
      const result = await apiPost(
        `/stripe/sales/${sale.id}/paylink/revoke?company_id=${company_id}`,
        {},
      );

      if (result?.existing_pages === "unconfirmed") {
        setPaymentError(result.message);
      }
      clearStickyPaylink(sale.id);
      await removeServerPaylinkTag();

      setSale((prev) =>
        prev
          ? {
              ...prev,
              via_paylink: false,
              via_gateway: false,
              paylink_url: null,
              status_raw: null,
              payment_status: null,
              gateway_provider: null,
              gateway_method: null,
            }
          : prev,
      );
      setPayLinkUrl(null);
      setShowPayLinkModal(false);

      await refreshSaleOnly();
    } catch (e: any) {
      setPaymentError(
        localApiDetail(e) || localMessage("sales.errors.cancelPayLink"),
      );
    }
  };

  // memo save
  const handleSaveMemo = async () => {
    if (!sale) return;
    setMemoSaving(true);
    setMemoError(null);
    try {
      await apiPatch(`/sales/${sale.id}/memo?company_id=${company_id}`, {
        memo: memoDraft,
      });
      setSale(
        (prev) =>
          prev && {
            ...prev,
            memo: memoDraft,
          },
      );
      setEditingMemo(false);
    } catch {
      setMemoError(localMessage("sales.errors.saveMemo"));
    } finally {
      setMemoSaving(false);
    }
  };

  // delete sale
  const handleDeleteSale = async () => {
    if (!sale) return;

    setDeleteError(null);

    try {
      await apiDelete(`/sales/${sale.id}?company_id=${company_id}`);
      setShowDelete(false);
      navigate(companyRoute(company_id!, "/sales"));
    } catch (e: any) {
      if (isClosedPeriodError(e)) {
        setDeleteError(localMessage("sales.errors.closedEdit"));
        return;
      }

      setDeleteError(localApiDetail(e) || localMessage("sales.errors.deleteSale"));
    }
  };

  async function getInvoicePublicUrl(
    invoiceId: string | number,
  ): Promise<string> {
    const res = await apiGet(`/invoices/${invoiceId}/share-link`);

    const publicUrl =
      (res as any)?.public_url || (res as any)?.publicUrl || (res as any)?.url;

    if (!publicUrl) {
      throw new LocalSalesError(localMessage("sales.errors.missingShareLink"));
    }

    return String(publicUrl);
  }

  const handleViewInvoice = async () => {
    if (!sale?.invoice_id) return;

    try {
      const publicUrl = await getInvoicePublicUrl(
        sale.invoice_id as string | number,
      );

      window.open(publicUrl, "_blank", "noopener,noreferrer");

      posthog.capture("invoice_viewed", {
        sale_id: sale.id,
        invoice_id: sale.invoice_id,
        company_id,
        source: "sale_detail",
      });
    } catch (e: any) {
      setSendInvoiceError(
        e?.response?.data?.detail || (e instanceof LocalSalesError ? e.display : e?.message) || localMessage("sales.errors.openInvoice"),
      );
    }
  };

  async function handleDownloadInvoiceHtml() {
    if (!sale?.invoice_id) return;

    try {
      const publicUrl = await getInvoicePublicUrl(
        sale.invoice_id as string | number,
      );
      const rawHtml = await fetch(publicUrl, {
        credentials: "omit",
      }).then(async (res) => {
        if (!res.ok) {
          throw new LocalSalesError(
            localMessage("sales.errors.fetchInvoiceHtml", { status: res.status }),
          );
        }
        return res.text();
      });
      const blob = new Blob([rawHtml], {
        type: "text/html;charset=utf-8",
      });

      const objUrl = URL.createObjectURL(blob);

      const niceName =
        (sale.invoice && sale.invoice.trim() !== ""
          ? sale.invoice.trim()
          : `invoice-${sale.invoice_id}`) + ".html";

      const a = document.createElement("a");
      a.href = objUrl;
      a.download = niceName;

      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objUrl);

      setSendInvoiceSuccess(localMessage("sales.errors.invoiceDownloaded"));
      setSendInvoiceError(null);
    } catch (e: any) {
      setSendInvoiceError(
        e?.response?.data?.detail || (e instanceof LocalSalesError ? e.display : e?.message) || localMessage("sales.errors.downloadInvoice"),
      );
    }

    posthog.capture("invoice_downloaded", {
      sale_id: sale.id,
      invoice_id: sale.invoice_id,
      company_id,
      source: "sale_detail",
    });
  }

  async function handleSendInvoiceBySettings() {
    if (!sale?.invoice_id) return;

    setSendingInvoice(true);
    setSendInvoiceError(null);
    setSendInvoiceSuccess(null);

    try {
      const res = await apiPost(
        `/invoices/${sale.invoice_id}/send?company_id=${company_id}`,
        {},
      );

      const action = String(res?.action || "")
        .toLowerCase()
        .trim();

      const channel = String(res?.channel || "")
        .toLowerCase()
        .trim();

      const whatsappUrl = String(res?.whatsapp_url || res?.url || "").trim();

      if (
        (action === "open_whatsapp" || channel === "whatsapp") &&
        whatsappUrl
      ) {
        const w = invoiceSendWindowRef.current;

        if (w && !w.closed) {
          try {
            w.location.href = whatsappUrl;
            w.focus();
          } catch {
            window.open(whatsappUrl, "_blank", "noopener,noreferrer");
          }
        } else {
          window.open(whatsappUrl, "_blank", "noopener,noreferrer");
        }

        posthog.capture("invoice_send_dispatched", {
          sale_id: sale.id,
          invoice_id: sale.invoice_id,
          company_id,
          source: "sale_detail",
          trigger_type: "manual",
          user_plan: isPro ? "pro" : "basic",
          channel: "whatsapp",
        });

        setSendInvoiceSuccess(
          res?.message ||
            localMessage("sales.errors.whatsappOpened"),
        );
        return;
      }

      closeInvoiceSendWindow();

      if (action === "email_sent" || channel === "email") {
        posthog.capture("invoice_send_dispatched", {
          sale_id: sale.id,
          invoice_id: sale.invoice_id,
          company_id,
          source: "sale_detail",
          trigger_type: "manual",
          user_plan: isPro ? "pro" : "basic",
          channel: "email",
        });

        setSendInvoiceSuccess(res?.message || localMessage("sales.errors.invoiceEmailed"));
        return;
      }

      posthog.capture("invoice_send_dispatched", {
        sale_id: sale.id,
        invoice_id: sale.invoice_id,
        company_id,
        source: "sale_detail",
        trigger_type: "manual",
        user_plan: isPro ? "pro" : "basic",
        channel: channel || "unknown",
      });

      setSendInvoiceSuccess(res?.message || localMessage("sales.errors.invoiceSent"));
    } catch (e: any) {
      closeInvoiceSendWindow();

      posthog.capture("invoice_send_dispatched", {
        sale_id: sale?.id,
        invoice_id: sale?.invoice_id,
        company_id,
        source: "sale_detail",
        trigger_type: "manual",
        user_plan: isPro ? "pro" : "basic",
        channel: "error",
      });

      const detail =
        e?.response?.data?.detail || (e instanceof LocalSalesError ? e.display : e?.message) || localMessage("sales.errors.sendInvoice");
      setSendInvoiceError(detail);
    } finally {
      setSendingInvoice(false);
    }
  }

  // edit button logic
  const handleEditClick = () => {
    if (canEdit) {
      navigate(companyRoute(company_id!, `/sales/${sale!.id}/edit`));
      return;
    }

    const reasons: SalesMessage[] = [];
    if (sale?.voided) reasons.push(localMessage("sales.errors.voidedSentence"));
    if (isPaid) reasons.push(localMessage("sales.errors.fullyPaid"));
    if (hasPayment) reasons.push(localMessage("sales.errors.receivedPayment"));
    if (ed && !ed.allowed && ed.reasons?.length) reasons.push(...ed.reasons);
    if (!reasons.length)
      reasons.push(localMessage("sales.errors.editingUnavailable"));

    setBlockedReasons(reasons);
    setShowBlockedEdit(true);
  };

  /* -------------------------
     Early return states
     ------------------------- */
  if (loading) {
    return <div className="text-center py-6">{t("sales.detail.loading")}</div>;
  }

  if (error) {
    return <div className="text-center py-6 text-red-500">{messageText(error)}</div>;
  }

  if (!sale) {
    return <div className="text-center py-6 text-gray-500">{t("sales.detail.notFound")}</div>;
  }

  /* -------------------------
     Render
     ------------------------- */
  return (
    <div
      className={`min-h-screen bg-gray-50 flex flex-col items-center pt-6 px-2 ${A.body}`}
    >
      {/* Page header bar */}
      <div className="sticky top-0 z-30 bg-gray-50 w-full max-w-6xl flex items-center border-b border-gray-100 px-2 pb-2 mb-2">
        <div className="flex-none">
          <IconButton aria-label={t("directory.common.back")} onClick={() => navigate(-1)}>
            <ArrowLeftIcon className="w-6 h-6 text-gray-400 hover:text-gray-600 transition" />
          </IconButton>
        </div>

        <div className="flex-1 flex justify-center pointer-events-none relative">
          <h2 className={`${A.title} text-center`}>{t("sales.detail.title")}</h2>
        </div>

        <div className="flex-none ml-auto">
          <IconButton
            aria-label={t("directory.common.close")}
            onClick={() => navigate(companyRoute(company_id!, "/"))}
          >
            <XMarkIcon className="w-6 h-6 text-gray-400 hover:text-gray-600 transition" />
          </IconButton>
        </div>
      </div>

      {/* Main card */}
      <AppleCard className="w-full max-w-3xl mx-auto" padding="p-6">
        <SaleInfoHeader
          labels={{
            view: t("sales.header.view"),
            download: t("sales.header.download"),
            payLinkDraft: t("sales.header.payLinkDraft"),
            payLinkSent: t("sales.header.payLinkSent"),
            payLinkOpened: t("sales.header.payLinkOpened"),
            payLinkPaid: t("sales.header.payLinkPaid"),
            payLinkExpired: t("sales.header.payLinkExpired"),
            payLink: t("sales.header.payLink"),
            sending: t("sales.header.sending"),
            send: t("sales.header.send"),
            payLinkAction: t("sales.header.payLinkAction"),
            upgradePro: t("sales.header.upgradePro"),
            viewInvoice: t("sales.header.viewInvoice"),
            downloadInvoice: t("sales.header.downloadInvoice"),
            openPayLink: t("sales.header.openPayLink"),
            sendInvoice: t("sales.header.sendInvoice"),
            cannotSend: t("sales.header.cannotSend"),
            addCustomerDetails: t("sales.header.addCustomerDetails"),
            dueDate: t("sales.common.dueDate"), date: t("sales.common.date"), invoice: t("sales.common.invoice"), customer: t("sales.common.customer"), outstanding: t("sales.common.outstanding"), total: t("sales.common.total")
          }}
          date={sale.date}
          invoice={sale.invoice}
          invoiceId={sale.invoice_id}
          customer={sale.customer?.name}
          email={sale.customer?.email}
          mobile={
            sale.customer?.mobile ||
            sale.customer?.phone ||
            sale.customer?.whatsapp ||
            undefined
          }
          onAddCustomer={() => openCustomerModal(false)}
          showAddCustomer={!sale.voided}
          statusBubbles={bubbles.map((bubble) => ({ ...bubble, id: bubble.bubbleType, label: ({
            paid: t("sales.detail.paid"), unpaid: t("sales.detail.unpaid"), voided: t("sales.detail.voided"),
            preorder: t("sales.detail.preorder"), fulfilled: t("sales.detail.fulfilled"), partial: t("sales.detail.partialPaid"),
          })[bubble.bubbleType] }))}
          showPayLinkPill={!!payLinkStatus}
          payLinkStatus={payLinkStatus}
          edited={sale.edited && !sale.voided}
          lastEditedAt={sale.last_edited_at}
          outstanding={outstanding}
          dueDate={headerDueDate}
          showInvoiceLink={true}
          total={sale.amount}
          isPaid={isPaid}
          onViewInvoice={handleViewInvoice}
          onDownloadInvoice={handleDownloadInvoiceHtml}
          onPayLink={() => setShowPayLinkModal(true)}
          showPayLinkAction={
            !!(payLinkUrl || sale.paylink_url) && !sale.voided && !isPaid
          }
          onSendInvoice={handleSendInvoiceClick}
          canSendInvoice={canSendInvoice}
          sendingInvoice={sendingInvoice}
          requiresPro={!isPro}
          sendBlockedReason={
            !isPro
              ? t("sales.errors.requiresPro")
              : !sale?.invoice_id
                ? t("sales.errors.noInvoiceSend")
                : sale?.voided
                  ? t("sales.errors.voided")
                  : !hasCustomerEmail && !hasCustomerMobile
                    ? t("sales.errors.addContact")
                    : undefined
          }
          onUpgrade={() => setShowUpgrade(true)}
        />

        {sendInvoiceError && (
          <div className="mb-3 rounded-xl bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">
            {messageText(sendInvoiceError)}
          </div>
        )}
        {sendInvoiceSuccess && (
          <div className="mb-3 rounded-xl bg-green-50 border border-green-200 px-3 py-2 text-xs text-green-700">
            {messageText(sendInvoiceSuccess)}
          </div>
        )}

        {hasPreOrderItem && !sale.voided && (
          <div className="mb-4 flex items-center">
            <Button
              variant="primary"
              minWidth="auto"
              disabled={fulfilling}
              onClick={handleFulfillPreOrder}
            >
              {fulfilling ? t("sales.detail.fulfilling") : t("sales.detail.fulfill")}
            </Button>
            {fulfillError && (
              <span className="ml-3 text-xs text-red-600">{messageText(fulfillError)}</span>
            )}
          </div>
        )}

        {hasStripeConflict && (
          <div className="mb-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] text-amber-800" role="status">
            {(sale.stripe_receipt_exceptions?.length ? sale.stripe_receipt_exceptions : [{
              id: 0,
              amount: String(Number(invoiceProviderMeta.stripe_amount_cents || 0) / 100),
              currency: String(invoiceProviderMeta.currency || "HKD").toUpperCase(),
              payment_reference: String(invoiceProviderMeta.stripe_pi_id || ""),
              reason: "legacy",
            }]).map((receipt) => (
              <div key={receipt.id} className="mb-2">
                <div className="font-semibold">{t("sales.detail.stripeConflict")}</div>
                <div>{t(receipt.reason === "stripe_success_after_detachment"
                    ? "sales.detail.stripeDetachedHelp"
                    : receipt.reason === "legacy" ? "sales.detail.stripeLegacyHelp" : "sales.detail.stripeConflictHelp", {
                      amount: Number(receipt.amount).toFixed(2), currency: receipt.currency,
                    })}</div>
                <div>{receipt.payment_reference}</div>
              </div>
            ))}
          </div>
        )}

        {/* ===== Line items ===== */}
        <div className="mb-5">
          {/* Desktop */}
          <div className="hidden sm:block">
            <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
              <table className="w-full text-[13px] text-gray-700">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-3 py-2 text-xs font-normal text-gray-500">
                      {t("sales.detail.productService")}
                    </th>
                    <th className="text-right px-3 py-2 text-xs font-normal text-gray-500">
                      {t("sales.common.qty")}
                    </th>
                    <th className="text-right px-3 py-2 text-xs font-normal text-gray-500">
                      {t("sales.common.unitPrice")}
                    </th>
                    <th className="text-right px-3 py-2 text-xs font-normal text-gray-500">
                      {t("sales.common.discount")}
                    </th>
                    <th className="text-right px-3 py-2 text-xs font-normal text-gray-500">
                      {t("sales.common.subtotal")}
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-100">
                  {sale.lines.map((line) => (
                    <tr key={line.id}>
                      <td className="px-3 py-2">
                        {line.item_kind === "service" ? (
                          <>
                            {line.product_name || line.detail || <i>{t("sales.common.service")}</i>}
                            <span
                              className={`ml-2 align-middle ${pillClass(
                                "info",
                              )}`}
                            >
                              {line.product_id ? t("sales.common.nonInventory") : t("sales.common.service")}
                            </span>
                          </>
                        ) : (
                          <>
                            {line.product_name}
                            {line.preorder && (
                              <span className="ml-2 text-xs text-gray-600 italic">
                                ({t("sales.detail.preorder")})
                              </span>
                            )}
                          </>
                        )}
                      </td>

                      <td className="text-right px-3 py-2 tabular-nums">
                        {line.qty}
                      </td>

                      <td className="text-right px-3 py-2 tabular-nums">
                        {money(line.unit_price)}
                      </td>

                      <td className="text-right px-3 py-2 tabular-nums">
                        {typeof line.discount === "number" &&
                        line.discount > 0 ? (
                          `${line.discount}%`
                        ) : (
                          <span className="invisible select-none">0%</span>
                        )}
                      </td>

                      <td className="text-right px-3 py-2 tabular-nums">
                        {money(calcSubtotal(line))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile */}
          <div className="block sm:hidden space-y-3">
            {sale.lines.map((line) => (
              <AppleCard key={line.id} padding="p-4">
                <div className="font-semibold text-gray-700 text-[13px] mb-1 flex items-center flex-wrap gap-2">
                  <span className="min-w-0 truncate">
                    {line.product_name || line.detail}
                  </span>

                  {line.item_kind === "service" && (
                    <span className={pillClass("info")}>
                      {line.product_id ? t("sales.common.nonInventory") : t("sales.common.service")}
                    </span>
                  )}

                  {line.preorder && (
                    <span className="text-xs text-gray-600 italic">
                      ({t("sales.detail.preorder")})
                    </span>
                  )}
                </div>

                <div className="flex flex-wrap text-[13px] text-gray-700">
                  <div className="w-1/2 mb-0.5">
                    <span className="text-gray-500">{t("sales.common.mobileQty")} </span>
                    <span className="tabular-nums">{line.qty}</span>
                  </div>

                  <div className="w-1/2 mb-0.5 text-right">
                    <span className="text-gray-500">{t("sales.common.mobileUnitPrice")} </span>
                    <span className="tabular-nums">
                      {money(line.unit_price)}
                    </span>
                  </div>

                  {typeof line.discount === "number" && line.discount > 0 ? (
                    <div className="w-1/2">
                      <span className="text-gray-500">{t("sales.common.mobileDiscount")} </span>
                      <span className="tabular-nums">{line.discount}%</span>
                    </div>
                  ) : (
                    <div className="w-1/2 invisible select-none">
                      <span className="text-gray-500">{t("sales.common.mobileDiscount")} </span>
                      <span>0%</span>
                    </div>
                  )}

                  <div className="w-1/2 text-right font-medium">
                    <span className="text-gray-500">{t("sales.common.mobileSubtotal")} </span>
                    <span className="tabular-nums">
                      {money(calcSubtotal(line))}
                    </span>
                  </div>
                </div>
              </AppleCard>
            ))}
          </div>
        </div>

        {/* ===== Totals ===== */}
        <div className="mb-4 text-right text-[13px] text-gray-500">
          {t("sales.common.totalWithColon")}{" "}
          <span className="font-semibold text-gray-700 tabular-nums">
            {money(sale.amount)}
          </span>
        </div>

        {/* ===== Payment History ===== */}
        {customerPayments.length > 0 && (
          <div className="mb-5">
            <div className={`${A.label} mb-2`}>{t("sales.detail.paymentHistory")}</div>

            {/* Desktop history */}
            <div className="hidden sm:block">
              <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
                <table className="w-full text-[13px] text-gray-700">
                  <thead className="bg-gray-50">
                    <tr className="text-xs text-gray-500">
                      <th className="text-left px-3 py-2 font-normal">{t("sales.common.date")}</th>
                      <th className="text-left px-3 py-2 font-normal">
                        {t("sales.detail.method")}
                      </th>
                      <th className="text-right px-3 py-2 font-normal tabular-nums">
                        {t("sales.common.amount")}
                      </th>
                      <th className="text-right px-3 py-2 font-normal tabular-nums">
                        {t("sales.common.fee")}
                      </th>
                      <th className="text-right px-3 py-2 font-normal tabular-nums">
                        {t("sales.common.net")}
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-100">
                    {customerPayments.map((p, i) => {
                      const method = paymentMethods.find(
                        (m) => m.id === p.payment_method_id,
                      );
                      const acct = accounts.find(
                        (a) => a.id === p.payment_account_id,
                      );

                      const feeVal =
                        typeof p.fee === "number" ? p.fee : Number(p.fee || 0);
                      const amountVal =
                        typeof p.amount === "number"
                          ? p.amount
                          : Number(p.amount || 0);
                      const net = amountVal - feeVal;

                      return (
                        <tr key={p.id || i}>
                          <td className="px-3 py-2">
                            {formatAnyDateToLocale(p.payment_date)}
                          </td>
                          <td className="px-3 py-2">
                            {method ? method.name : acct ? acct.name : "—"}
                          </td>
                          <td className="text-right px-3 py-2 tabular-nums">
                            {money(amountVal)}
                          </td>
                          <td className="text-right px-3 py-2 tabular-nums">
                            {money(feeVal)}
                          </td>
                          <td className="text-right px-3 py-2 tabular-nums">
                            {money(net)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Mobile history */}
            <div className="block sm:hidden space-y-2">
              {customerPayments.map((p, i) => {
                const method = paymentMethods.find(
                  (m) => m.id === p.payment_method_id,
                );
                const acct = accounts.find(
                  (a) => a.id === p.payment_account_id,
                );

                const feeVal =
                  typeof p.fee === "number" ? p.fee : Number(p.fee || 0);
                const amountVal =
                  typeof p.amount === "number"
                    ? p.amount
                    : Number(p.amount || 0);
                const net = amountVal - feeVal;

                return (
                  <AppleCard key={p.id || i} padding="p-4">
                    <div className="flex justify-between">
                      <span className={`${A.label}`}>{t("sales.common.date")}</span>
                      <span className="font-medium">
                        {formatAnyDateToLocale(p.payment_date)}
                      </span>
                    </div>

                    <div className="flex justify-between mt-1">
                      <span className={`${A.label}`}>{t("sales.detail.method")}</span>
                      <span className="font-medium">
                        {method ? method.name : acct ? acct.name : "—"}
                      </span>
                    </div>

                    <div className="flex justify-between mt-1">
                      <span className={`${A.label}`}>{t("sales.common.amount")}</span>
                      <span className="font-medium tabular-nums">
                        {money(amountVal)}
                      </span>
                    </div>

                    <div className="flex justify-between mt-1">
                      <span className={`${A.label}`}>{t("sales.common.fee")}</span>
                      <span className="font-medium tabular-nums">
                        {money(feeVal)}
                      </span>
                    </div>

                    <div className="flex justify-between mt-1">
                      <span className={`${A.label}`}>{t("sales.common.net")}</span>
                      <span className="font-medium tabular-nums">
                        {money(net)}
                      </span>
                    </div>
                  </AppleCard>
                );
              })}
            </div>
          </div>
        )}

        {totalPaid > 0 && (
          <div className="text-right text-[13px] text-gray-700 mb-5">
            {t("sales.common.paidTotal")}{" "}
            <span className="font-medium tabular-nums">{money(totalPaid)}</span>
          </div>
        )}

        {paymentError && (
          <div role="alert" className="text-xs text-red-500 mb-3 whitespace-pre-line">{messageText(paymentError)}</div>
        )}

        {/* ===== Pay Link notice ===== */}
        {payLinkPending && !sale.voided && !isPaid && (
          <div className="mb-3 bg-amber-50 border border-amber-200 px-3 py-2 rounded-2xl text-[13px] text-amber-900">
            <Trans
              i18nKey="sales.detail.pendingLink"
              components={{ cancel: <button
                type="button"
                className="text-red-600 underline underline-offset-2 font-medium hover:text-red-700"
                onClick={handleCancelPayLink}
              /> }}
            />
          </div>
        )}

        {/* Conversion selects a new Sale method; collection below excludes deferred types. */}
        {isPendingGatewaySale && !sale.voided && !editingMemo && (
          <div className="mb-4">
            <label className={`block mb-1 ${A.label}`}>{t("sales.detail.convertOffline")}</label>
            <AppleComboBox
                  emptyText={t("sales.shared.noResults")}
                  preserveQueryOnDisplayChange
                  toggleOptionsLabel={t("sales.shared.toggleOptions")}
              options={paymentMethods.filter((pm) => !isStripeOnline(pm))}
              value={null}
              onChange={(pm) => { if (pm) handlePaymentMethodChange(pm.id); }}
              getOptionLabel={(pm) => pm.name}
              placeholder={t("sales.detail.selectConversionMethod")}
            />
          </div>
        )}

        {/* ===== Add Payment section ===== */}
        {!sale.voided && !isPaid && !editingMemo && !payLinkPending && (
          <div className="bg-gray-50 rounded-2xl px-4 py-4 ring-1 ring-black/5 mb-4 w-full">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {/* Payment date */}
              <div>
                <label className={`block mb-1 ${A.label}`}>{t("sales.common.paymentDate")}</label>
                <AppleDateInput openCalendarLabel={t("sales.shared.openCalendar")}                   valueYmd={paymentDate}
                  onChangeYmd={(ymd) => setPaymentDate(ymd)}
                  ariaLabel={t("sales.common.paymentDate")}
                  maxYmd={ymdTodayLocal()}
                />
                {isClosedPaymentDate && (
                  <div className="mt-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-[13px] text-amber-800">
                    {t("sales.errors.closedWarning")}
                  </div>
                )}
              </div>

              {/* Payment method */}
              <div>
                <label className={`block mb-1 ${A.label}`}>
                  {t("sales.common.paymentMethod")}
                </label>
                <AppleComboBox
                  emptyText={t("sales.shared.noResults")}
                  preserveQueryOnDisplayChange
                  toggleOptionsLabel={t("sales.shared.toggleOptions")}
                  options={visiblePaymentMethods}
                  value={selectedPM || null}
                  onChange={(pm) => {
                    const nextId = pm ? pm.id : null;
                    handlePaymentMethodChange(nextId);
                  }}
                  getOptionSearchText={(pm) => isStripeOnline(pm) ? "Stripe" : pm.name}
                  getOptionLabel={(pm) => isStripeOnline(pm) ? t("sales.detail.stripeOption") : pm.name}
                  placeholder={t("sales.detail.selectPaymentMethod")}
                />
              </div>

              {/* Amount */}
              <div>
                <label className={`block mb-1 ${A.label}`}>
                  {selectedIsStripe ? t("sales.detail.chargeAmount") : t("sales.common.amount")}
                </label>
                <AppleNumberInput
                  value={paymentAmount}
                  onChange={(e) => {
                    autoPayAmountRef.current.active = false;
                    setPaymentAmount(e.target.value);
                  }}
                  placeholder="0.00"
                  min={0}
                  step="0.01"
                  className="text-right tabular-nums"
                />
              </div>
            </div>

            {/* Auto fee + override (non-Stripe, non-cash only). FEE-RATE-
                SEMANTICS-1 Phase 2B: cash exposes no override and always posts
                explicit zero -- no configured rate/fixed fee. */}
            {!selectedIsStripe && !selectedIsCash && (
              <>
                <div className="mt-3 flex items-center justify-between text-xs text-gray-600">
                  <div>
                    {t("sales.detail.autoFee", { amount: money(autoFee) })}
                  </div>
                  <button
                    type="button"
                    className="underline underline-offset-2 hover:text-gray-800"
                    onClick={() => setShowFeeOverride((v) => !v)}
                  >
                    {showFeeOverride ? t("sales.detail.hideOverride") : t("sales.detail.override")}
                  </button>
                </div>

                {showFeeOverride && (
                  <div className="mt-3">
                    <label className={`block mb-1 ${A.label}`}>
                      {t("sales.detail.processingFee")}
                    </label>
                    <AppleNumberInput
                      value={feeOverride}
                      onChange={(e) => setFeeOverride(e.target.value)}
                      placeholder={autoFee ? autoFee.toFixed(2) : "0.00"}
                      min={0}
                      step="0.01"
                    />
                  </div>
                )}
              </>
            )}

            {/* Stripe guidance text */}
            {selectedIsStripe && (
              <div className="mt-3 text-[13px] text-gray-600">
                {t("sales.detail.stripeCollectionHelp")}
              </div>
            )}

            {/* Action buttons */}
            <div className="flex items-center justify-end mt-4 gap-2">
              {!selectedIsStripe ? (
                <Button
                  onClick={handleMarkPaid}
                  disabled={
                    paying || switchingPaymentMethod || !selectedPaymentMethodId
                  }
                  variant="primary"
                  minWidth="auto"
                >
                  {paying ? t("sales.detail.adding") : t("sales.detail.addPayment")}
                </Button>
              ) : (
                <>
                  <Button
                    onClick={handleGetPayLink}
                    disabled={!selectedPaymentMethodId || outstanding <= 0}
                    variant="outline"
                    minWidth="auto"
                  >
                    {t("sales.detail.createPayLink")}
                  </Button>

                  <Button
                    onClick={handleChargeWithStripe}
                    disabled={!selectedPaymentMethodId || outstanding <= 0}
                    variant="primary"
                    minWidth="auto"
                  >
                    {t("sales.detail.chargeStripe")}
                  </Button>
                </>
              )}
            </div>
          </div>
        )}

        {/* ===== Memo ===== */}
        {!editingMemo ? (
          <MemoSection label={t("sales.common.memo")} placeholder={t("sales.common.memoPlaceholder")}
          editAriaLabel={t("sales.common.editMemo")}
            mode="view"
            variant="card"
            value={sale.memo || ""}
            emptyText={t("sales.detail.noMemo")}
            className="mb-6"
            onEditClick={
              !canEdit
                ? () => {
                    setEditingMemo(true);
                    setMemoDraft(sale.memo || "");
                    setMemoError(null);
                  }
                : undefined
            }
          />
        ) : (
          <AppleCard padding="p-4" className="mb-6">
            <MemoSection
          editAriaLabel={t("sales.common.editMemo")}
              mode="edit"
              variant="plain"
              label={t("sales.common.memo")}
              value={memoDraft}
              onChange={setMemoDraft}
              rows={2}
              disabled={memoSaving}
              placeholder={t("sales.common.memoPlaceholder")}
            />

            <div className="flex gap-3 justify-end mt-3">
              <Button
                variant="secondary"
                type="button"
                onClick={() => {
                  setEditingMemo(false);
                  setMemoDraft(sale.memo || "");
                  setMemoError(null);
                }}
                disabled={memoSaving}
              >
                {t("directory.common.cancel")}
              </Button>

              <Button
                variant="primary"
                type="button"
                onClick={handleSaveMemo}
                disabled={memoSaving}
              >
                {memoSaving ? t("directory.common.savingEllipsis") : t("directory.common.save")}
              </Button>
            </div>

            <div className="min-h-[18px] mt-2">
              {memoError ? (
                <div className="text-xs text-red-500 font-medium">
                  {messageText(memoError)}
                </div>
              ) : null}
            </div>
          </AppleCard>
        )}

        {/* ===== Bottom actions ===== */}
        <div className="flex justify-end gap-3 mt-6">
          <Button
            variant={canEdit ? "outline" : "secondary"}
            type="button"
            onClick={handleEditClick}
            aria-disabled={!canEdit}
            title={canEdit ? undefined : messageText(editDisallowedReason)}
            className={canEdit ? "" : "bg-gray-50 text-gray-400"}
          >
            {t("directory.common.edit")}
          </Button>

          <Button
            variant="secondary"
            type="button"
            onClick={() => {
              if (!sale.voided) setShowDelete(true);
            }}
            disabled={sale.voided}
            title={sale.voided ? t("sales.errors.alreadyVoided") : t("directory.common.delete")}
          >
            {sale.voided ? t("sales.detail.voided") : t("directory.common.delete")}
          </Button>
        </div>

        {/* ===== PayLink modal ===== */}
        {showPayLinkModal && (
          <div
            className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40 p-4"
            role="dialog"
            aria-modal="true"
          >
            <AppleCard className="w-full max-w-lg shadow-md" padding="p-0">
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                <h3 className={`${A.title}`}>{t("sales.detail.paymentLink")}</h3>
                <IconButton
                  aria-label={t("directory.common.close")}
                  onClick={handlePayLinkDone}
                  className="h-9 w-9"
                >
                  <XMarkIcon className="w-5 h-5 text-gray-400 hover:text-gray-600 transition" />
                </IconButton>
              </div>

              <div className="px-5 py-4 space-y-3">
                <div className="text-[13px] text-gray-600">
                  {t("sales.detail.shareLinkHelp")}
                </div>

                <div className="flex items-center gap-2">
                  <AppleInput
                    value={payLinkUrl || sale.paylink_url || ""}
                    readOnly
                    onFocus={(e) => e.currentTarget.select()}
                  />

                  <Button
                    type="button"
                    variant="secondary"
                    minWidth="78px"
                    className="h-10"
                    onClick={async () => {
                      const effectiveUrl = payLinkUrl || sale.paylink_url;
                      if (!effectiveUrl) return;
                      const ok = await copyTextSafe(effectiveUrl);
                      if (ok) {
                        setCopiedPaylink(true);
                        window.setTimeout(() => setCopiedPaylink(false), 2000);
                      }
                    }}
                  >
                    {copiedPaylink ? t("sales.common.copied") : t("sales.detail.copy")}
                  </Button>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="secondary"
                    minWidth="78px"
                    onClick={() => {
                      const effectiveUrl = payLinkUrl || sale.paylink_url;
                      if (!effectiveUrl) return;
                      window.open(
                        effectiveUrl,
                        "_blank",
                        "noopener,noreferrer",
                      );
                    }}
                  >
                    {t("sales.detail.open")}
                  </Button>

                  {"share" in navigator && (payLinkUrl || sale.paylink_url) ? (
                    <Button
                      type="button"
                      variant="secondary"
                      minWidth="78px"
                      onClick={async () => {
                        try {
                          await (navigator as any).share({
                            title: "Payment link",
                            url: payLinkUrl || sale.paylink_url,
                          });
                        } catch {}
                      }}
                    >
                      {t("sales.detail.share")}
                    </Button>
                  ) : null}

                  <div className="flex-1" />

                  <Button
                    onClick={handlePayLinkDone}
                    variant="primary"
                    minWidth="100px"
                  >
                    {t("sales.common.done")}
                  </Button>
                </div>
              </div>
            </AppleCard>
          </div>
        )}

        {showStripeQrModal && (
          <div
            className="fixed inset-0 z-[1100] bg-black/40 p-3 sm:p-4 flex items-center justify-center"
            role="dialog"
            aria-modal="true"
          >
            <AppleCard
              className="w-full max-w-lg max-h-[90vh] overflow-auto shadow-md"
              padding="p-0"
            >
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                <h3 className={A.title}>{t("sales.common.scanToPay")}</h3>
                <IconButton
                  aria-label={t("directory.common.close")}
                  onClick={closeStripeCheckout}
                  className="h-9 w-9"
                >
                  <XMarkIcon className="w-5 h-5 text-gray-400 hover:text-gray-600 transition" />
                </IconButton>
              </div>

              <div className="px-5 py-4 space-y-4">
                <div className="text-[13px] text-gray-600">
                  {t("sales.common.scanInstructions")}
                </div>

                <div className="flex justify-center">
                  <div className="rounded-xl border border-gray-200 bg-white p-3">
                    {stripeCheckoutUrl ? (
                      <QRCodeSVG value={stripeCheckoutUrl} size={220} />
                    ) : null}
                  </div>
                </div>

                <div className="space-y-2">
                  <Button
                    type="button"
                    variant="secondary"
                    className="w-full"
                    onClick={handleCopyCheckoutLink}
                    disabled={!stripeCheckoutUrl}
                  >
                    {copiedCheckoutUrl ? t("sales.common.copied") : t("sales.common.copyLink")}
                  </Button>

                  <div className="text-xs text-gray-500 text-center">
                    {t("sales.common.noCustomerPhone")}
                  </div>

                  {stripeCheckoutUrl && (
                    <a
                      href={stripeCheckoutUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`${A.btnBase} ${A.btnSecondary} w-full`}
                      style={{ minWidth: "120px" }}
                    >
                      <span className="inline-flex items-center justify-center gap-2">
                        <span className="whitespace-nowrap">{t("sales.common.enterCard")}</span>
                      </span>
                    </a>
                  )}

                  <Button
                    type="button"
                    variant="primary"
                    className="w-full"
                    onClick={closeStripeCheckout}
                  >
                    {t("sales.common.done")}
                  </Button>
                </div>
              </div>
            </AppleCard>
          </div>
        )}

        {/* ===== Delete Modal ===== */}
        {showDelete && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-3">
            <AppleCard className="w-full max-w-sm shadow-md" padding="p-4">
              <div className={`${A.title}`}>{t("sales.detail.deleteConfirm")}</div>
              <div className="text-[13px] text-gray-600 mt-1">
                {t("directory.common.irreversible")}
              </div>

              {deleteError && (
                <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-center text-[13px] text-red-700 whitespace-pre-wrap">
                  {messageText(deleteError)}
                </div>
              )}

              <div className="flex justify-end gap-3 mt-4">
                <Button
                  variant="secondary"
                  type="button"
                  onClick={() => {
                    setDeleteError(null);
                    setShowDelete(false);
                  }}
                >
                  {t("directory.common.cancel")}
                </Button>

                <Button
                  variant="danger"
                  type="button"
                  onClick={handleDeleteSale}
                >
                  {t("directory.common.delete")}
                </Button>
              </div>
            </AppleCard>
          </div>
        )}

        {/* ===== Blocked Edit Modal ===== */}
        {showBlockedEdit && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm px-4">
            <AppleCard className="w-full max-w-md shadow-md" padding="p-6">
              <div className={`${A.title} mb-2`}>{t("sales.detail.editingBlocked")}</div>

              <ul className="list-disc pl-5 text-[13px] text-gray-700 space-y-1">
                {blockedReasons.map((r, i) => (
                  <li key={i}>{messageText(r)}</li>
                ))}

                {ed?.pro_override === true && !me?.is_user_pro && (
                  <li className="italic text-gray-600">
                    {t("sales.detail.proEditWindow")}
                  </li>
                )}
              </ul>

              <div className="flex justify-end gap-2 mt-4">
                <Button
                  variant="secondary"
                  type="button"
                  onClick={() => setShowBlockedEdit(false)}
                  minWidth="auto"
                >
                  {t("sales.detail.ok")}
                </Button>
              </div>
            </AppleCard>
          </div>
        )}

        {/* ===== Preorder Reminder ===== */}
        {hasPreOrderItem && (
          <div className="mt-8 flex items-center justify-center bg-amber-50 border border-amber-200 px-4 py-2 rounded-2xl text-amber-800 font-medium text-[13px]">
            {t("sales.detail.preorderHelp")}
          </div>
        )}

        {showCustomerModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4">
            <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl">
              <div className="mb-4">
                <h3 className={A.title}>
                  {sendAfterCustomerSave
                    ? t("sales.detail.addCustomerSend")
                    : t("sales.detail.addCustomer")}
                </h3>
                <p className="mt-1 text-xs text-gray-500">
                  {sendAfterCustomerSave
                    ? t("sales.detail.customerSendHelp")
                    : t("sales.detail.customerHelp")}
                </p>
              </div>

              <div className="space-y-3">
                <div>
                  <label className={`block mb-1 ${A.label}`}>{t("directory.common.name")}</label>
                  <AppleInput
                    value={customerNameDraft}
                    onChange={(e) => setCustomerNameDraft(e.target.value)}
                    placeholder={t("sales.detail.customerName")}
                  />
                </div>

                <div>
                  <label className={`block mb-1 ${A.label}`}>{t("directory.common.email")}</label>
                  <AppleInput
                    value={customerEmailDraft}
                    onChange={(e) => setCustomerEmailDraft(e.target.value)}
                    placeholder={t("sales.detail.optional")}
                  />
                </div>

                <div>
                  <label className={`block mb-1 ${A.label}`}>{t("directory.common.mobile")}</label>
                  <AppleInput
                    value={customerMobileDraft}
                    onChange={(e) => setCustomerMobileDraft(e.target.value)}
                    placeholder={t("sales.detail.optional")}
                  />
                </div>
              </div>

              {customerError && (
                <div className="mt-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                  {messageText(customerError)}
                </div>
              )}

              <div className="mt-5 flex justify-end gap-2">
                <Button
                  variant="secondary"
                  minWidth="auto"
                  onClick={() => {
                    setShowCustomerModal(false);
                    setSendAfterCustomerSave(false);
                    setCustomerError(null);
                  }}
                  disabled={customerSaving}
                >
                  {t("directory.common.cancel")}
                </Button>

                <Button
                  variant="primary"
                  minWidth="auto"
                  onClick={handleSaveCustomerDetails}
                  disabled={customerSaving}
                >
                  {customerSaving
                    ? t("directory.common.savingDots")
                    : sendAfterCustomerSave
                      ? t("sales.detail.saveAndSend")
                      : t("directory.common.save")}
                </Button>
              </div>
            </div>
          </div>
        )}

        {showUpgrade ? (
          <UpgradeModal closeLabel={t("directory.common.close")} labels={{
            title: t("sales.upgrade.title"),
            price: t("sales.upgrade.price"),
            description: t("sales.upgrade.description"),
            staff: <Trans i18nKey="sales.upgrade.staff" components={{ emphasis0: <span className="font-medium text-gray-700" />, emphasis1: <span className="font-medium text-gray-600" /> }} />,
            emails: <Trans i18nKey="sales.upgrade.emails" components={{ emphasis0: <span className="font-medium text-gray-700" />, emphasis1: <span className="font-medium text-gray-600" /> }} />,
            whatsapp: t("sales.upgrade.whatsapp"),
            branding: t("sales.upgrade.branding"),
            receiptScanner: <Trans i18nKey="sales.upgrade.receiptScanner" components={{ emphasis0: <span className="font-medium text-gray-700" />, emphasis1: <span className="font-medium text-gray-600" /> }} />,
            embeddedLinks: <Trans i18nKey="sales.upgrade.embeddedLinks" components={{ emphasis0: <span className="font-medium text-gray-700" />, emphasis1: <span className="font-medium text-gray-600" /> }} />,
            fees: t("sales.upgrade.fees"),
            gatewayHelp: <Trans i18nKey="sales.upgrade.gatewayHelp" components={{ emphasis0: <span className="font-medium text-gray-600" />, emphasis1: <span className="font-medium text-gray-600" /> }} />,
            enterCode: t("sales.upgrade.enterCode"),
            invitationCode: t("sales.upgrade.invitationCode"),
            notNow: t("sales.upgrade.notNow"),
            opening: t("sales.upgrade.opening"),
            startPro: t("sales.upgrade.startPro"),
            alreadyPro: t("sales.upgrade.alreadyPro"),
            billingPortal: t("sales.upgrade.billingPortal"),
            secure: t("sales.upgrade.secure"),
            timeout: t("sales.upgrade.timeout"),
            activating: t("sales.upgrade.activating"),
            active: t("sales.upgrade.active"),
            used: t("sales.upgrade.used"),
            ineligible: t("sales.upgrade.ineligible"),
            invalidCode: t("sales.upgrade.invalidCode"),
            startTrialFailed: t("sales.upgrade.startTrialFailed"),
            checkoutFailed: t("sales.upgrade.checkoutFailed"),
            missingPortalUrl: t("sales.upgrade.missingPortalUrl"),
            portalFailed: t("sales.upgrade.portalFailed"),
            dismiss: t("sales.upgrade.dismiss")
          }} open={true} onClose={() => setShowUpgrade(false)} />
        ) : null}
      </AppleCard>
    </div>
  );
}
